import { useEffect, useMemo, useState } from "react";
import { Clock, Check, X as XIcon, Pencil, ArrowDown } from "lucide-react";

// =====================================================================
// PhaseTimingRow — "Begins X weeks after Phase Y" annotation editor.
//
// Renders inline below the phase-tabs row when a non-first phase tab is
// active. Phase 1 is the implicit start (no timing); only phases ≥2
// can be annotated.
//
// Storage:  treatment_plans.phase_meta jsonb keyed by phase number, e.g.
//   { "2": { "offset_days": 14, "after_phase": 1 } }
//
// On save, calls `onSetTiming(phase_number, offset_days, after_phase)`
// — the parent translates to the set_phase_timing RPC. Pass null /
// 0 for offset_days to clear.
//
// Visual: a prominent teal pill instead of muted prose. The interval
// number is a chip the dentist can't miss while scanning the plan.
// Edit mode exposes quick-pick chips (3d / 1w / 2w / 1m / 3m / 6m)
// because most post-procedure intervals are one of these six.
// =====================================================================

// Common clinical intervals. Days values must match what's saved.
const QUICK_CHIPS = [
  { label: "3 days",   days: 3   },
  { label: "1 week",   days: 7   },
  { label: "2 weeks",  days: 14  },
  { label: "1 month",  days: 30  },
  { label: "3 months", days: 90  },
  { label: "6 months", days: 180 },
];

export default function PhaseTimingRow({
  activePhase,                 // number (we only render when typeof === "number")
  phaseNumbers = [],           // [1, 2, 3] — used to populate the "after Phase X" dropdown
  phaseNames   = {},
  phaseMeta    = {},           // { "2": { offset_days, after_phase }, ... }
  onSetTiming,                 // (phase_number, offset_days|null, after_phase|null) => Promise
  disabled = false,
}) {
  // Defensive — handle string-encoded numbers from JSON quirks.
  const phaseNum = (() => {
    if (typeof activePhase === "number" && Number.isInteger(activePhase) && activePhase > 0) return activePhase;
    if (typeof activePhase === "string") {
      const n = Number(activePhase);
      if (Number.isInteger(n) && n > 0) return n;
    }
    return null;
  })();
  const isPhase = phaseNum != null;
  const meta    = isPhase ? (phaseMeta?.[String(phaseNum)] || null) : null;

  const earlierPhases = useMemo(
    () => phaseNumbers.filter((n) => n < (phaseNum ?? 0)),
    [phaseNumbers, phaseNum],
  );
  const defaultAfter = meta?.after_phase ?? (phaseNum ? phaseNum - 1 : 1);

  const [editing, setEditing] = useState(false);
  const [num, setNum]   = useState(2);
  const [unit, setUnit] = useState("weeks");
  const [after, setAfter] = useState(defaultAfter);

  // Reset the inline form whenever the phase or meta changes.
  useEffect(() => {
    setEditing(false);
    if (meta) {
      const days = meta.offset_days ?? 0;
      if (days % 7 === 0 && days >= 7) {
        setNum(days / 7); setUnit("weeks");
      } else {
        setNum(days);     setUnit("days");
      }
      setAfter(meta.after_phase ?? (phaseNum - 1));
    } else if (isPhase) {
      setNum(2);
      setUnit("weeks");
      setAfter(phaseNum - 1);
    }
  }, [phaseNum, meta, isPhase]);

  if (!isPhase) return null;             // Hidden on "All" tab

  // ---- Phase 1: implicit start. Small pill, no editor. ----
  if (phaseNum === 1) {
    return (
      <div
        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-500 border border-slate-200"
        data-testid="phase-timing-row-phase1"
      >
        <Clock className="w-3 h-3" />
        Begins immediately
      </div>
    );
  }

  const refLabel = (n) => phaseNames?.[String(n)] || `Appointment ${n}`;
  const hasMultipleEarlier = earlierPhases.length > 1;

  // ---- Display mode — prominent teal pill ----
  if (!editing) {
    const configured = !!meta?.offset_days;
    return (
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
          configured
            ? "bg-teal-50 border-teal-200 text-slate-700"
            : "bg-amber-50 border-amber-200 text-amber-900"
        }`}
        data-testid="phase-timing-row"
      >
        <ArrowDown className={`w-4 h-4 shrink-0 ${configured ? "text-teal-600" : "text-amber-600"}`} />
        {configured ? (
          <span className="text-sm flex items-center gap-1.5 flex-wrap">
            <span className="text-slate-600">Begins</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-white text-teal-700 font-bold border border-teal-300 text-sm">
              {formatOffset(meta.offset_days)}
            </span>
            <span className="text-slate-600">after</span>
            <span className="font-semibold text-slate-900">{refLabel(meta.after_phase)}</span>
          </span>
        ) : (
          <span className="text-sm font-medium">
            Set appointment timing
          </span>
        )}
        {!disabled ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid="phase-timing-edit"
            title={configured ? "Change timing" : "Set appointment timing"}
            className={`ml-auto inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-md ${
              configured
                ? "text-teal-700 hover:bg-teal-100"
                : "text-amber-700 hover:bg-amber-100"
            }`}
          >
            <Pencil className="w-3 h-3" />
            {configured ? "Change" : "Set"}
          </button>
        ) : null}
      </div>
    );
  }

  // ---- Edit mode ----
  const currentDays = unit === "weeks" ? num * 7 : num;
  const activeChipDays = QUICK_CHIPS.find((c) => c.days === currentDays)?.days ?? null;
  const isCustom = activeChipDays == null;

  async function commit(daysOverride) {
    const offsetDays = daysOverride != null ? daysOverride : (unit === "weeks" ? num * 7 : num);
    if (!Number.isFinite(offsetDays) || offsetDays < 0) return;
    const targetAfter = earlierPhases.includes(after) ? after : earlierPhases[earlierPhases.length - 1];
    if (targetAfter == null) return;
    await onSetTiming?.(phaseNum, offsetDays, targetAfter);
    setEditing(false);
  }
  async function clearTiming() {
    await onSetTiming?.(phaseNum, 0, 0);   // backend treats 0 / null as clear
    setEditing(false);
  }
  function pickChip(days) {
    if (days % 7 === 0 && days >= 7) { setNum(days / 7); setUnit("weeks"); }
    else                              { setNum(days);    setUnit("days");  }
    // Commit on chip click — quick path. If user wants custom, they tap Custom.
    commit(days);
  }

  return (
    <div
      className="flex flex-col gap-2 px-3 py-2.5 rounded-lg border border-teal-300 bg-teal-50"
      data-testid="phase-timing-row-editing"
    >
      <div className="flex items-center gap-1.5 text-xs font-semibold text-teal-800 uppercase tracking-wide">
        <Clock className="w-3.5 h-3.5" />
        When does {refLabel(phaseNum)} begin?
      </div>

      {/* Quick-pick chip row */}
      <div className="flex flex-wrap items-center gap-1.5">
        {QUICK_CHIPS.map((chip) => {
          const isActive = chip.days === activeChipDays;
          return (
            <button
              key={chip.days}
              type="button"
              onClick={() => pickChip(chip.days)}
              data-testid={`phase-timing-chip-${chip.days}`}
              className={`px-2.5 py-1 text-xs font-semibold rounded-md border transition-colors ${
                isActive
                  ? "bg-teal-600 border-teal-700 text-white"
                  : "bg-white border-teal-200 text-teal-700 hover:bg-teal-100"
              }`}
            >
              {chip.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => {
            // Switch to custom — leave num/unit as-is, user edits the fields below.
            if (!isCustom) { setNum(meta?.offset_days || 14); setUnit("days"); }
          }}
          data-testid="phase-timing-chip-custom"
          className={`px-2.5 py-1 text-xs font-semibold rounded-md border ${
            isCustom
              ? "bg-teal-600 border-teal-700 text-white"
              : "bg-white border-teal-200 text-teal-700 hover:bg-teal-100"
          }`}
        >
          Custom
        </button>
      </div>

      {/* Custom number + unit + after-phase dropdown (only if 2+ earlier phases) */}
      {(isCustom || hasMultipleEarlier) ? (
        <div className="flex items-center gap-1.5 flex-wrap text-xs text-slate-700">
          {isCustom ? (
            <>
              <span className="text-slate-600">Begins</span>
              <input
                type="number"
                min={0}
                max={999}
                value={num}
                onChange={(e) => setNum(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
                data-testid="phase-timing-num"
                className="w-16 px-1.5 py-1 text-xs rounded border border-slate-300 focus:outline-none focus:border-teal-500"
              />
              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                data-testid="phase-timing-unit"
                className="text-xs rounded border border-slate-300 bg-white px-1.5 py-1"
              >
                <option value="days">days</option>
                <option value="weeks">weeks</option>
              </select>
            </>
          ) : null}
          {hasMultipleEarlier ? (
            <>
              <span className="text-slate-600">after</span>
              <select
                value={after}
                onChange={(e) => setAfter(Number(e.target.value))}
                data-testid="phase-timing-after"
                className="text-xs rounded border border-slate-300 bg-white px-1.5 py-1"
              >
                {earlierPhases.map((n) => (
                  <option key={n} value={n}>{refLabel(n)}</option>
                ))}
              </select>
            </>
          ) : null}
          {isCustom ? (
            <button
              type="button"
              onClick={() => commit()}
              data-testid="phase-timing-save"
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md bg-teal-600 text-white hover:bg-teal-700"
            >
              <Check className="w-3.5 h-3.5" /> Save
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Footer actions */}
      <div className="flex items-center gap-2">
        {meta?.offset_days ? (
          <button
            type="button"
            onClick={clearTiming}
            data-testid="phase-timing-clear"
            className="text-[11px] font-semibold uppercase tracking-wide text-rose-600 hover:text-rose-700"
          >
            Clear timing
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => setEditing(false)}
          data-testid="phase-timing-cancel"
          className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-slate-700"
        >
          <XIcon className="w-3.5 h-3.5" /> Cancel
        </button>
      </div>
    </div>
  );
}

// "14 days" → "2 weeks", "21 days" → "3 weeks", "5 days" → "5 days".
export function formatOffset(days) {
  const n = Math.max(0, Math.floor(days || 0));
  if (n === 0) return "0 days";
  if (n % 30 === 0 && n >= 30) {
    const m = n / 30;
    return `${m} ${m === 1 ? "month" : "months"}`;
  }
  if (n % 7 === 0) {
    const w = n / 7;
    return `${w} ${w === 1 ? "week" : "weeks"}`;
  }
  return `${n} ${n === 1 ? "day" : "days"}`;
}
